import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbot } from '../../context/ChatbotContext';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { MessageSquare, X, Minimize2, Send, Maximize2 } from 'lucide-react';
import Button from '../ui/Button';
import clsx from 'clsx';

const Chatbot: React.FC = () => {
  const { t } = useTranslation();
  const { chatState, isOpen, toggleChat, closeChat, sendMessage, setChatState, getMessagesLeft } = useChatbot();
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [chatState.messages, prefersReducedMotion]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageInput.trim()) {
      sendMessage(messageInput);
      setMessageInput('');
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={toggleChat}
        className="fixed z-50 rounded-full bg-primary p-4 text-white shadow-lg transition-all duration-300 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 motion-reduce:transition-none bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] right-[max(1.5rem,env(safe-area-inset-right,0px))]"
        aria-label={t('chatbot.openLabel')}
      >
        <MessageSquare size={24} />
      </button>
    );
  }

  return (
    <div className={clsx(
      'fixed z-50 flex flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-xl animate-fade-in transition-all duration-300 motion-reduce:animate-none motion-reduce:transition-none',
      chatState.isExpanded
        ? 'bottom-[10vh] left-[10vw] right-[10vw] top-[10vh]'
        : 'bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] right-[max(1.5rem,env(safe-area-inset-right,0px))] h-[min(31rem,calc(100dvh-5.5rem))] max-h-[calc(100dvh-5.5rem)] w-[calc(100vw-1.5rem)] max-w-md sm:w-full'
    )}>
      {/* Chatbot Header */}
      <div className="flex items-center justify-between border-b-2 border-accent bg-primary px-4 py-3 text-white">
        <div className="flex items-center space-x-2">
          <MessageSquare size={20} />
          <h3 className="text-sm">{t('chatbot.title')}</h3>
          <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
            {getMessagesLeft()} {t('chatbot.messagesLeft')}
          </span>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setChatState(prev => ({ ...prev, isExpanded: !prev.isExpanded }))}
            className="rounded-full p-1.5 transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            aria-label={chatState.isExpanded ? t('chatbot.minimizeLabel') : t('chatbot.expandLabel')}
          >
            {chatState.isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            type="button"
            onClick={closeChat}
            className="rounded-full p-1.5 transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            aria-label={t('chatbot.closeLabel')}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
        {chatState.messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 ${
              message.isUser ? 'flex justify-end' : 'flex justify-start'
            }`}
          >
            <div
              className={`max-w-[75%] p-3 rounded-lg ${
                message.isUser
                  ? 'bg-primary text-white rounded-tr-none'
                  : 'rounded-tl-none border border-slate-200/90 bg-white text-slate-800'
              }`}
            >
              <p className="whitespace-pre-line">{message.content}</p>
              <span
                className={`text-xs block mt-1 ${
                  message.isUser ? 'text-white/75' : 'text-slate-500'
                }`}
              >
                {message.timestamp instanceof Date 
                  ? message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                }
              </span>
            </div>
          </div>
        ))}

        {chatState.isLoading && (
          <div className="mb-4 flex justify-start">
            <div className="rounded-lg rounded-tl-none border border-slate-200/90 bg-white p-3 text-slate-800">
              <div className="flex space-x-2" aria-hidden>
                <div
                  className={clsx(
                    'h-2 w-2 rounded-full bg-accent/60',
                    !prefersReducedMotion && 'animate-pulse'
                  )}
                />
                <div
                  className={clsx(
                    'h-2 w-2 rounded-full bg-primary/50 delay-150',
                    !prefersReducedMotion && 'animate-pulse'
                  )}
                />
                <div
                  className={clsx(
                    'h-2 w-2 rounded-full bg-accent/60 delay-300',
                    !prefersReducedMotion && 'animate-pulse'
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {chatState.error && (
          <div className="mb-4 rounded-lg bg-error-light p-3 text-sm text-error">
            {chatState.error}
          </div>
        )}

        <div ref={messagesEndRef}></div>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="border-t border-slate-200/90 p-3">
        <div className="flex space-x-2">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder={t('chatbot.placeholder')}
            className="input flex-1"
            disabled={chatState.isLoading}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={chatState.isLoading || !messageInput.trim()}
            icon={<Send size={18} />}
          >
            {t('chatbot.send')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Chatbot;